import React, { useState, useEffect, useCallback} from 'react';
import { Loader2 } from 'lucide-react';

const SummaryTab = ({ api }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [grandTotals, setGrandTotals] = useState({ total: 0 });

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/records/summary');
      const data = res.data || [];

      // --- 1. Find all unique status headers ---
      const allStatuses = new Set();
      data.forEach(row => {
        row.statuses.forEach(s => allStatuses.add(s.status));
      });
      const sortedHeaders = Array.from(allStatuses).sort();
      setHeaders(sortedHeaders);

      // --- 2. Set the rows data ---
      setRows(data);

      // --- 3. Calculate Grand Totals for the footer ---
      const totals = {};
      let totalOfTotals = 0;

      // Calculate totals for each status column
      sortedHeaders.forEach(header => {
        let headerTotal = 0;
        data.forEach(row => {
          const status = row.statuses.find(s => s.status === header);
          if (status) {
            headerTotal += status.count;
          }
        });
        totals[header] = headerTotal;
      });

      // Calculate the final Grand Total
      data.forEach(row => {
        totalOfTotals += row.totalCount;
      });
      totals['Grand Total'] = totalOfTotals;

      setGrandTotals(totals);

    } catch (err) {
      console.error('Error fetching summary:', err);
      setError('Failed to load summary data. ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 text-gray-500 animate-spin" />
        <span className="ml-3 text-gray-600">Loading summary...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-6 p-4 rounded-xl border-l-4 bg-red-50 border-red-500 text-red-800">
        {error}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No summary data available.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-300 shadow-md">
      <table className="min-w-full divide-y divide-gray-300">
        {/* Table Header */}
        <thead className="bg-cyan-900 text-white">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
              Provider
            </th>
            {headers.map(header => (
              <th key={header} scope="col" className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
                {header}
              </th>
            ))}
            <th scope="col" className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
              Grand Total
            </th>
          </tr>
        </thead>

        {/* Table Body */}
        <tbody className="bg-white divide-y divide-gray-200">
          {rows.map((row, idx) => (
            <tr key={row.provider} className={idx % 2 === 0 ? 'bg-cyan-50' : 'bg-white'}>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                {row.provider}
              </td>
              {headers.map(header => {
                const status = row.statuses.find(s => s.status === header);
                const count = status ? status.count : '';
                return (
                  <td key={`${row.provider}-${header}`} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                    {count}
                  </td>
                );
              })}
              <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-800">
                {row.totalCount}
              </td>
            </tr>
          ))}
        </tbody>

        {/* Table Footer (Grand Totals) */}
        <tfoot className="bg-cyan-900 text-white">
          <tr>
            <th scope="row" className="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
              Grand Total
            </th>
            {headers.map(header => (
              <td key={`total-${header}`} className="px-6 py-3 text-left text-sm font-bold">
                {grandTotals[header] || 0}
              </td>
            ))}
            <td className="px-6 py-3 text-left text-sm font-bold">
              {grandTotals['Grand Total'] || 0}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default SummaryTab;